import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Field, Input, makeStyles, tokens } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { useAcceptInvitation } from "../hooks/useIdentityMutations";
import { useNotify } from "../../../shared/components/SuperToast";
import LanguageComboBox from "../../../shared/components/LanguageComboBox";

import appIcon from "../../../assets/logo.svg";

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.colorNeutralBackground3,
    minHeight: "100vh",
    width: "100%",
  },
  form: {
    width: "400px",
    padding: "32px",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: "8px",
    boxShadow: tokens.shadow16,
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    marginBottom: "24px",
  },
  logo: {
    width: "64px",
    height: "64px",
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    margin: 0,
    textAlign: "center",
  },
  field: {
    marginBottom: "16px",
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: "24px",
  },
  link: {
    textAlign: "center",
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorBrandForeground1,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  successMessage: {
    padding: "16px",
    backgroundColor: tokens.colorPaletteGreenBackground2,
    borderRadius: "4px",
    marginBottom: "16px",
    textAlign: "center",
    color: tokens.colorPaletteGreenForeground2,
  },
});

export default function IdentityOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const styles = useStyles();
  const { notify } = useNotify();
  const { t } = useTranslation();

  const token = searchParams.get("token");
  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [language, setLanguage] = useState("en");
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const handleSuccess = (data) => {
    setSuccess(true);
    notify(data.message || t("identity.onboardingSuccess", "Onboarding completed successfully!"), "success");
    setTimeout(() => {
      navigate("/login");
    }, 3000);
  };

  const handleError = (error) => {
    notify(error.message || t("identity.onboardingFailed", "Failed to complete onboarding"), "error");
  };

  const acceptInvitationMutation = useAcceptInvitation(handleSuccess, handleError, true);

  const validateForm = () => {
    const errors = {};

    if (!fname?.trim()) {
      errors.fname = t("validation.required", "This field is required");
    }

    if (!lname?.trim()) {
      errors.lname = t("validation.required", "This field is required");
    }

    if (!displayName?.trim()) {
      errors.displayName = t("validation.required", "This field is required");
    }

    if (!password) {
      errors.password = t("validation.required", "This field is required");
    } else if (password.length < 8) {
      errors.password = t("validation.passwordMinLength", "Password must be at least 8 characters");
    }

    if (!confirmPassword) {
      errors.confirmPassword = t("validation.required", "This field is required");
    } else if (password !== confirmPassword) {
      errors.confirmPassword = t("Phrases.passwordMismatch", "Passwords do not match");
    }

    if (!language) {
      errors.language = t("validation.required", "This field is required");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!token) {
      notify(t("identity.missingToken", "Missing invitation token"), "error");
      return;
    }

    const userInfo = {
      fname: fname.trim(),
      lname: lname.trim(),
      name: displayName.trim(),
      password,
      language,
    };

    acceptInvitationMutation.mutate({ tokenHash: token, userInfo });
  };

  // Show error if no token
  if (!token) {
    return (
      <div className={styles.container}>
        <div className={styles.form}>
          <div className={styles.header}>
            <img src={appIcon} alt="Logo" className={styles.logo} />
            <h1 className={styles.title}>{t("identity.invalidInvitation", "Invalid Invitation")}</h1>
            <p className={styles.subtitle}>{t("identity.missingTokenMessage", "No invitation token provided")}</p>
          </div>
          <div className={styles.footer}>
            <div className={styles.link} onClick={() => navigate("/login")}>
              {t("nounPhrases.backToLogin", "Back to Login")}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <div className={styles.header}>
          <img src={appIcon} alt="Logo" className={styles.logo} />
          <h1 className={styles.title}>{t("generic.registration", "Registration")}</h1>
        </div>

        {success && (
          <div className={styles.successMessage}>
            {t("identity.onboardingSuccessMessage", "Onboarding completed! Redirecting to login...")}
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit}>
            <Field
              className={styles.field}
              label={t("generic.fname", "First Name")}
              required
              validationMessage={validationErrors.fname}
              validationState={validationErrors.fname ? "error" : undefined}
            >
              <Input
                type="text"
                value={fname}
                onChange={(e) => setFname(e.target.value)}
                placeholder={t("generic.fname", "First Name")}
                disabled={acceptInvitationMutation.isPending}
              />
            </Field>

            <Field
              className={styles.field}
              label={t("generic.lname", "Last Name")}
              required
              validationMessage={validationErrors.lname}
              validationState={validationErrors.lname ? "error" : undefined}
            >
              <Input
                type="text"
                value={lname}
                onChange={(e) => setLname(e.target.value)}
                placeholder={t("generic.lname", "Last Name")}
                disabled={acceptInvitationMutation.isPending}
              />
            </Field>

            <Field
              className={styles.field}
              label={t("generic.name", "Display Name")}
              required
              validationMessage={validationErrors.displayName}
              validationState={validationErrors.displayName ? "error" : undefined}
            >
              <Input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t("generic.name", "Display Name")}
                disabled={acceptInvitationMutation.isPending}
              />
            </Field>

            <Field
              className={styles.field}
              label={t("nounPhrases.newPassword", "New Password")}
              required
              validationMessage={validationErrors.password}
              validationState={validationErrors.password ? "error" : undefined}
            >
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("nounPhrases.newPassword", "New Password")}
                disabled={acceptInvitationMutation.isPending}
              />
            </Field>

            <Field
              className={styles.field}
              label={t("nounPhrases.confirmPassword", "Confirm Password")}
              required
              validationMessage={validationErrors.confirmPassword}
              validationState={validationErrors.confirmPassword ? "error" : undefined}
            >
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t("nounPhrases.confirmPassword", "Confirm Password")}
                disabled={acceptInvitationMutation.isPending}
              />
            </Field>

            <Field
              className={styles.field}
              label={t("generic.language", "Language")}
              required
              validationMessage={validationErrors.language}
              validationState={validationErrors.language ? "error" : undefined}
            >
              <LanguageComboBox value={language} onOptionSelect={(code) => setLanguage(code)} />
            </Field>

            <div className={styles.footer}>
              <Button appearance="primary" type="submit" disabled={acceptInvitationMutation.isPending}>
                {acceptInvitationMutation.isPending
                  ? t("generic.sending", "Sending") + "..."
                  : t("generic.send", "Send")}
              </Button>

              <div className={styles.link} onClick={() => navigate("/login")}>
                {t("nounPhrases.backToLogin", "Back to Login")}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
